const { getDevice } = require('utils/device');

const internals = {
    bugTypes: [
        'Report a Bug',
        'Request Support',
        'Report Inappropriate Use',
        'Make a Suggestion',
        'Suggest a New Interest',
        'Suggest a New Conversation Starter',
        'Close My Account',
        'Dashboard Help'
    ],
    deviceTypes: [
        'Web Browser',
        'iPhone/iPad',
        'Android phone',
        'Windows laptop/desktop',
        'Apple laptop/desktop',
        'Other'
    ],
    browserTypes: [
        'Google Chrome',
        'Mozilla Firefox',
        'Microsoft Edge',
        'Safari',
        'Opera',
        'Internet Explorer',
        'N/A'
    ]
};

internals.deviceTypeByPlatform = {
    ios: 1,
    android: 2,
    windows: 3,
    mac_os: 4
};

internals.browserTypeByBrowser = {
    chrome: 0,
    firefox: 1,
    edge: 2,
    safari: 3,
    opera: 4,
    ie: 5
};

internals.hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

internals.getDefaultDeviceType = ({ platform, browser } = {}) => {

    if (internals.hasOwn(internals.deviceTypeByPlatform, platform)) {
        return internals.deviceTypeByPlatform[platform];
    }

    if (browser && browser !== 'app' && browser !== 'unknown') {
        return 0;
    }

    if (platform && platform !== 'unknown') {
        return 5;
    }

    return '';
};

internals.getDefaultBrowserType = (browser) => {

    if (internals.hasOwn(internals.browserTypeByBrowser, browser)) {
        return internals.browserTypeByBrowser[browser];
    }

    if (browser) {
        return 6;
    }

    return '';
};

internals.getSupportEnvironmentDefaults = () => {

    try {
        const [platform, browser] = String(getDevice()).split(':');

        return {
            deviceType: internals.getDefaultDeviceType({ platform, browser }),
            browserType: internals.getDefaultBrowserType(browser)
        };
    }
    catch (err) {
        return {
            deviceType: '',
            browserType: ''
        };
    }
};

internals.toNumericType = (bugType) => {

    if (bugType === '' || bugType === null || typeof bugType === 'undefined') {
        return null;
    }

    return Number(bugType);
};

internals.isSuggestNewInterest = (bugType) => internals.toNumericType(bugType) === 4;
internals.isSuggestNewConversationStarter = (bugType) => internals.toNumericType(bugType) === 5;
internals.isCloseMyAccount = (bugType) => internals.toNumericType(bugType) === 6;
internals.isSpecialDetailsFlow = (bugType) => internals.isCloseMyAccount(bugType)
    || internals.isSuggestNewInterest(bugType)
    || internals.isSuggestNewConversationStarter(bugType);

internals.optionFromIndexedLabel = (label, index) => ({
    value: index,
    label
});

internals.buildBugTypeOptions = ({ isAdmin }) => internals.bugTypes
    .map(internals.optionFromIndexedLabel)
    .filter((option) => !String(option.label).includes('Dashboard') || isAdmin);

internals.buildIndexedOptions = (labels) => labels.map(internals.optionFromIndexedLabel);

internals.buildCategoryOptions = (categories = []) => categories.map((category) => ({
    value: category.name,
    label: category.name
}));

internals.getBugDetailsCopy = (bugType) => {

    if (internals.isCloseMyAccount(bugType)) {
        return {
            label: 'Reason for closing account?',
            validationLabel: 'Reason for closing account',
            placeholder: 'Add reason for closing account here'
        };
    }

    if (internals.isSuggestNewConversationStarter(bugType)) {
        return {
            label: 'New Conversation Starter',
            validationLabel: 'Conversation starter text',
            placeholder: 'Enter conversation starter text'
        };
    }

    if (internals.isSuggestNewInterest(bugType)) {
        return {
            label: 'New Interest',
            validationLabel: 'New Interest name',
            placeholder: 'Enter new Interest name'
        };
    }

    return {
        label: 'Description',
        validationLabel: 'Description',
        placeholder: 'Add details here'
    };
};

internals.buildFields = ({
    bugType,
    categories = [],
    isAdmin = false,
    onBugTypeChange
} = {}) => {

    const bugDetailsCopy = internals.getBugDetailsCopy(bugType);
    const isSpecialDetailsFlow = internals.isSpecialDetailsFlow(bugType);

    return [
        {
            type: 'SelectField',
            name: 'bugType',
            label: 'I want to...',
            options: internals.buildBugTypeOptions({ isAdmin }),
            onChange: (nextBugType) => {

                if (typeof onBugTypeChange === 'function') {
                    onBugTypeChange(nextBugType);
                }
            },
            rules: {
                validate: (value) => {

                    if (value === null || typeof value === 'undefined' || value === '') {
                        return 'This field is required';
                    }

                    return true;
                }
            },
            textFieldProps: {
                id: 'bugTypeSelect',
                SelectProps: {
                    MenuProps: {
                        className: 'report-dropdown'
                    }
                }
            }
        },
        {
            type: 'TextareaField',
            name: 'bugDetails',
            label: bugDetailsCopy.label,
            validationLabel: bugDetailsCopy.validationLabel,
            required: true,
            textFieldProps: {
                id: 'bugDetails',
                placeholder: bugDetailsCopy.placeholder,
                maxRows: 4,
                inputProps: {
                    'aria-label': bugDetailsCopy.label
                }
            }
        },
        internals.isSuggestNewInterest(bugType) && {
            type: 'SelectField',
            name: 'suggestedInterestCategory',
            label: 'New Interest Category',
            validationLabel: 'New Interest Category',
            required: true,
            options: internals.buildCategoryOptions(categories),
            textFieldProps: {
                id: 'suggestedInterestCategory',
                SelectProps: {
                    MenuProps: {
                        className: 'report-dropdown'
                    }
                }
            }
        },
        !isSpecialDetailsFlow && {
            type: 'SelectField',
            name: 'deviceType',
            label: 'Device type',
            options: internals.buildIndexedOptions(internals.deviceTypes),
            textFieldProps: {
                id: 'deviceType',
                SelectProps: {
                    MenuProps: {
                        className: 'device-dropdown'
                    }
                }
            }
        },
        !isSpecialDetailsFlow && {
            type: 'SelectField',
            name: 'browserType',
            label: 'Browser type',
            options: internals.buildIndexedOptions(internals.browserTypes),
            textFieldProps: {
                id: 'browserType',
                SelectProps: {
                    MenuProps: {
                        className: 'browser-dropdown'
                    }
                }
            }
        }
    ];
};

internals.buildDefaults = ({
    bugType = '',
    redirectUrl = '/app/welcome'
} = {}) => {

    const { deviceType, browserType } = internals.getSupportEnvironmentDefaults();

    return {
        bugType,
        bugDetails: '',
        suggestedInterestCategory: '',
        deviceType,
        browserType,
        redirectUrl
    };
};

internals.getIndexedValue = (labels, value) => labels[value] || '';

module.exports.getInitialBugType = function getInitialBugType({
    location
} = {}) {

    if (location?.state?.isSuggestNewInterest) {
        return 4;
    }

    if (location?.state?.isAdminContact) {
        return 7;
    }

    return '';
};

module.exports.getInitialRedirectUrl = function getInitialRedirectUrl({
    location
} = {}) {

    return location?.state?.comeFromEditPage
        ? '/app/profile/edit/interests'
        : '/app/welcome';
};

module.exports.buildFormConfig = function buildFormConfig(props = {}) {

    const {
        defaults,
        formId,
        onInvalidSubmit,
        onSubmit,
        ...fieldOptions
    } = props;

    return {
        defaults,
        fields: internals.buildFields(fieldOptions),
        formId,
        onInvalidSubmit,
        onSubmit
    };
};

module.exports.buildSubmitPayload = function buildSubmitPayload({
    values
} = {}) {

    const isSuggestNewInterest = internals.isSuggestNewInterest(values.bugType);

    return {
        bugType: internals.getIndexedValue(internals.bugTypes, values.bugType),
        bugDetails: {
            description: values.bugDetails,
            suggestedInterestCategory: isSuggestNewInterest ? values.suggestedInterestCategory : '',
            deviceType: internals.getIndexedValue(internals.deviceTypes, values.deviceType),
            browserType: internals.getIndexedValue(internals.browserTypes, values.browserType),
            role: '',
            schoolName: ''
        }
    };
};

module.exports.internals = internals;
